import { reduxForm } from 'redux-form';
import { array, ord, string } from 'fp-ts';
import { LocalizedText, useLocalizeText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { presence, SelectField, StepperField, validation } from '@mews-commander/legacy-form';
import { createOption, ResponsiveTwoColumnsFormFields } from '@mews-commander/ui';
import { KeyCutter } from '@mews-commander/data-access';
import { isEveryKeyCutterUsable } from './availability';

interface KeyCutterFormFieldsProps {
    showKeyCutterCount?: boolean;
    keyCutters: KeyCutter[];
    /** Affects which keycutters will be disabled, because some of them need reservation to be checked-in. */
    isCheckedIn: boolean;
}

export const constraints = {
    keyCount: { presence },
    keyCutter: { presence },
};

const keyCountProps = {
    label: <LocalizedText textKey={TextKey.KeyCount} />,
    name: 'keyCount',
    required: true,
};
const keyCutterProps = {
    label: <LocalizedText textKey={TextKey.KeyCutter} />,
    name: 'keyCutter',
    required: true,
};

const createOptions = ({ keyCutters, isCheckedIn }: { keyCutters: KeyCutter[]; isCheckedIn: boolean }) => {
    const sortedKeyCutters = array.sort(ord.contramap((cutter: KeyCutter) => cutter.name || '')(string.Ord))(
        keyCutters
    );

    const sortedMappedKeyCutters = sortedKeyCutters.map((keyCutter) =>
        createOption({
            value: keyCutter.id ?? null,
            label: keyCutter.name,
            disabled: !isCheckedIn && keyCutter.requiresCheckIn,
        })
    );

    return sortedMappedKeyCutters;
};

export const KeyCutterFormFields = ({
    showKeyCutterCount = false,
    keyCutters,
    isCheckedIn,
}: KeyCutterFormFieldsProps) => {
    const localizeText = useLocalizeText();

    const options = createOptions({ keyCutters, isCheckedIn });

    const everyKeyCuttersUsable = isEveryKeyCutterUsable({ keyCutters, isCheckedIn });

    const helper = !everyKeyCuttersUsable
        ? localizeText({ textKey: TextKey.KeyCutterFormKeyCuttersAvailableOnlyAfterCheckIn })
        : null;

    return (
        <ResponsiveTwoColumnsFormFields>
            {showKeyCutterCount && <StepperField {...keyCountProps} min={0} />}
            <SelectField {...keyCutterProps} helper={helper} options={options} />
        </ResponsiveTwoColumnsFormFields>
    );
};

export const KeyCutterForm = reduxForm({ validate: validation(constraints) })(KeyCutterFormFields);
